import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Client } from '../../http';
import { RootState } from '../../app/store';

/**
 * Community Fibre orders. A DGtek sign up ends in an installer booking, so
 * there is nothing to watch; Community Fibre build the service themselves over
 * days, so each order is followed until it is live, cancelled or refused. This
 * is the page that shows where each one is up to, and the one thing that can be
 * done about it: call it off.
 */

export interface CfOrder {
  order_id: string;
  service_id: string;
  network: string;
  status: string;
  /** Community Fibre's own word for where the order is. */
  external_state?: string;
  /** The Community Fibre API it went through: mef or npis. */
  order_api?: string;
  community_fibre_order_id?: string;
  /** Our reference on their order. */
  reference?: string;
  plan_id?: string;
  /** The product ordered: on MEF the Layer 3 BSM, on NPIS the Layer 2 OVC. */
  product?: string;
  /** The Layer 2 OVC a MEF order paired the BSM with. */
  access_product?: string;
  /** The in-service product they assign once it is live. */
  service_number?: string;
  customer?: string;
  email?: string;
  address?: string;
  /** The unit, as Community Fibre know the place. */
  gnaf_id?: string;
  /** The building the unit is in: how the CRM keys a Community Fibre building. */
  building_gnaf_id?: string;
  /** The day the customer asked for. */
  preferred_date?: string;
  /** The day Community Fibre confirmed they will connect it, which moves. */
  expected_date?: string;
  /** The day they did. */
  completed_date?: string;
  /** The interval the qualification quoted, in days (the SQ's installationDays). */
  installation_days?: number;
  /** The service as built, once it is. */
  service?: CfServiceDetails;
  hubspot_deal_id?: string;
  hubspot_building_id?: string;
  /** The portal's type id for a Building, for linking to the record. */
  hubspot_building_object_type?: string;
  /** The ticket every move is commented on. */
  hubspot_ticket_id?: string;
  reason?: string;
  created_at: number;
  updated_at: number;
  /** The order can be called off, or the live service disconnected. */
  cancellable: boolean;
  /** The live service can be moved to another plan (MEF only). */
  changeable: boolean;
  /** The orders placed against the live service, oldest first. */
  changes?: CfChangeOrder[];
}

/**
 * A change against a live service. Community Fibre take a plan change or a
 * disconnection as a product order of their own, with its own id, which is
 * followed until they apply it (observed 21 October 2026, SD-1021585).
 */
export interface CfChangeOrder {
  /** Community Fibre's id for the change order. */
  id: string;
  /** modify (a plan change) or delete (a disconnection). */
  kind: string;
  /** The plan a modify moves the service to. */
  plan_id?: string;
  external_plan_id?: string;
  access_plan_id?: string;
  /** Their word for where the change order is. */
  state?: string;
  /** Theirs, when they rejected or failed it. */
  reason?: string;
  trigger?: string;
  /** Unix seconds. */
  requested_at: number;
  /** Unix seconds, once they applied, rejected or failed it. */
  completed_at?: number;
}

/**
 * The service as Community Fibre built it: the OVC identifier, the UNI port
 * in the apartment with the customer VLAN mapped to it, and the ENNI on our
 * side with the service VLAN.
 */
export interface CfServiceDetails {
  id?: string;
  type?: string;
  uni?: string;
  uni_vlan?: number;
  enni?: string;
  enni_vlan?: number;
}

/** One note Community Fibre wrote on their order. */
export interface CfWholesalerNote {
  id?: string;
  /** Unix seconds. */
  at?: number;
  text: string;
  author?: string;
}

/** One thing that happened to an order, oldest first. */
export interface CfOrderEvent {
  /** Unix seconds. */
  at: number;
  from?: string;
  to: string;
  /** Who moved it: signup, communityfibre (the poller), portal. */
  trigger: string;
  detail?: string;
}

/** The whole Order record, which is what the card shows. */
export interface CfOrderDetail {
  id: string;
  status: string;
  network?: string;
  kind?: string;
  service_id: string;
  plan_id?: string;
  external_plan_id?: string;
  access_plan_id?: string;
  technology?: string;
  add_ons?: string[];
  gnaf_id?: string;
  building_gnaf_id?: string;
  formatted_address?: string;
  contact?: { first_name?: string; last_name?: string; email?: string; phone?: string };
  preferred_date?: string;
  expected_date?: string;
  completed_date?: string;
  installation_days?: number;
  service?: CfServiceDetails;
  wholesaler_notes?: CfWholesalerNote[];
  hubspot_ticket_id?: string;
  cb_subscription_id?: string;
  cb_customer_id?: string;
  hubspot_deal_id?: string;
  hubspot_building_id?: string;
  hubspot_contact_id?: string;
  external_order_id?: string;
  order_api?: string;
  external_ref?: string;
  external_state?: string;
  external_product?: string;
  reason?: string;
  events?: CfOrderEvent[];
  changes?: CfChangeOrder[];
  created_at: number;
  updated_at: number;
  completed_at?: number;
}

/** The statuses an order stops at. A live service is one of them until a
 * change is asked for against it. */
const TERMINAL = ['active', 'cancelled', 'ceased', 'failed'];

export const isTerminal = (status: string): boolean => TERMINAL.includes(status);

/** The statuses that have their own words. */
const LABELS: Record<string, string> = {
  cancel_requested: 'Cancellation requested',
  change_requested: 'Plan change requested',
  cease_requested: 'Disconnection requested',
};

/** The status as a person reads it, not as the record spells it. */
export const statusLabel = (status: string): string => {
  if (LABELS[status]) return LABELS[status];
  const words = status.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export interface CfOrdersState {
  orders: CfOrder[];
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
  /** The order a cancellation is in flight for, so one row spins. */
  cancelling: string | null;
  cancelError: string | null;
  /** The order a plan change is in flight for. */
  changing: string | null;
  changeError: string | null;
  /** The order whose card is open, and its whole record once it arrives. */
  openId: string | null;
  detail: CfOrderDetail | null;
  detailStatus: 'idle' | 'loading' | 'failed';
  detailError: string | null;
}

export const initialCfOrdersState: CfOrdersState = {
  orders: [],
  status: 'idle',
  error: null,
  cancelling: null,
  cancelError: null,
  changing: null,
  changeError: null,
  openId: null,
  detail: null,
  detailStatus: 'idle',
  detailError: null,
};

export const fetchCfOrders = createAsyncThunk<
CfOrder[],
{ openOnly?: boolean } | undefined,
{ rejectValue: string }
>(
  'cfOrders/fetch',
  async (arg, { rejectWithValue }) => {
    try {
      const url = arg?.openOnly
        ? '/api/v1/admin/orders?status=open'
        : '/api/v1/admin/orders';
      const response = await Client.get<{ orders: CfOrder[] }>(url);
      return response.data.orders ?? [];
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const msg = e?.response?.data?.error ?? 'Failed to load Community Fibre orders';
      return rejectWithValue(msg);
    }
  },
);

/** The whole record of one order, for its card. */
export const fetchCfOrder = createAsyncThunk<
CfOrderDetail,
string,
{ rejectValue: string }
>(
  'cfOrders/fetchOne',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await Client.get<CfOrderDetail>(`/api/v1/admin/orders/${orderId}`);
      return response.data;
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const msg = e?.response?.data?.error ?? 'Failed to load the order';
      return rejectWithValue(msg);
    }
  },
);

/**
 * Ask Community Fibre to drop an order. They accept the request without
 * applying it, so the answer is "cancellation requested" and the poller
 * watches for them to apply it.
 */
export const cancelCfOrder = createAsyncThunk<
CfOrder,
string,
{ rejectValue: string }
>(
  'cfOrders/cancel',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await Client.post<CfOrder>(`/api/v1/admin/orders/${orderId}/cancel`);
      return response.data;
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const msg = e?.response?.data?.error ?? 'Failed to cancel the order';
      return rejectWithValue(msg);
    }
  },
);

/**
 * Move a live service to another plan. Community Fibre take it as an order
 * of their own, so the answer is "plan change requested" and the poller
 * watches for them to apply it; the service stays on its current plan until
 * then.
 */
export const changeCfOrderPlan = createAsyncThunk<
CfOrder,
{ orderId: string; planId: string },
{ rejectValue: string }
>(
  'cfOrders/changePlan',
  async ({ orderId, planId }, { rejectWithValue }) => {
    try {
      const response = await Client.post<CfOrder>(`/api/v1/admin/orders/${orderId}/change`, { plan_id: planId });
      return response.data;
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const msg = e?.response?.data?.error ?? 'Failed to change the plan';
      return rejectWithValue(msg);
    }
  },
);

/** The row that answered replaces its own, and the card when it is the one open. */
const replaceRow = (state: CfOrdersState, row: CfOrder) => {
  state.orders = state.orders.map((o) => (o.order_id === row.order_id ? row : o));
  if (state.detail && state.detail.id === row.order_id) {
    state.detail.status = row.status;
    state.detail.external_state = row.external_state;
    state.detail.changes = row.changes;
  }
};

export const cfOrdersSlice = createSlice({
  name: 'cfOrders',
  initialState: initialCfOrdersState,
  reducers: {
    clearCfOrderError(state) {
      state.error = null;
      state.cancelError = null;
      state.changeError = null;
    },
    closeCfOrder(state) {
      state.openId = null;
      state.detail = null;
      state.detailStatus = 'idle';
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCfOrders.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCfOrders.fulfilled, (state, action) => {
        state.status = 'idle';
        state.error = null;
        state.orders = action.payload;
      })
      .addCase(fetchCfOrders.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Failed to load Community Fibre orders';
      })
      .addCase(cancelCfOrder.pending, (state, action) => {
        state.cancelling = action.meta.arg;
        state.cancelError = null;
      })
      .addCase(cancelCfOrder.fulfilled, (state, action) => {
        state.cancelling = null;
        // Only the row that changed, so the page does not jump.
        replaceRow(state, action.payload);
      })
      .addCase(changeCfOrderPlan.pending, (state, action) => {
        state.changing = action.meta.arg.orderId;
        state.changeError = null;
      })
      .addCase(changeCfOrderPlan.fulfilled, (state, action) => {
        state.changing = null;
        replaceRow(state, action.payload);
      })
      .addCase(changeCfOrderPlan.rejected, (state, action) => {
        state.changing = null;
        state.changeError = action.payload ?? 'Failed to change the plan';
      })
      .addCase(fetchCfOrder.pending, (state, action) => {
        state.openId = action.meta.arg;
        state.detail = null;
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchCfOrder.fulfilled, (state, action) => {
        // A slow answer for a row that is no longer open is dropped.
        if (action.meta.arg !== state.openId) return;
        state.detail = action.payload;
        state.detailStatus = 'idle';
      })
      .addCase(fetchCfOrder.rejected, (state, action) => {
        if (action.meta.arg !== state.openId) return;
        state.detailStatus = 'failed';
        state.detailError = action.payload ?? 'Failed to load the order';
      })
      .addCase(cancelCfOrder.rejected, (state, action) => {
        state.cancelling = null;
        state.cancelError = action.payload ?? 'Failed to cancel the order';
      });
  },
});

export const { clearCfOrderError, closeCfOrder } = cfOrdersSlice.actions;

export const selectCfOrders = (state: RootState) => state.cfOrders;

export default cfOrdersSlice.reducer;
